<script lang="ts">
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import RequestedItemBanner from "@shared/svelte/Components/RequestableItems/RequestedItemBanner.svelte";
  import RetailerDistributorStore from "@retailer/svelte/stores/RetailerDistributorStore";

  import type { CartProduct } from "@shared/types/ProductLine";
  import type { RetailerOrder } from "@shared/types/Order";
  import { isOHLQOrder } from "@retailer/services/order_fulfillment_service";
  import { getStockSiteLocation } from "@shared/services/retailer_user_authentication_service";

  export let order: RetailerOrder;
  export let showStreamlinedAOOActionRequired: boolean = false;

  const salesRepsString = (order: RetailerOrder) => {
    return order.sales_reps.map(sr => sr.name).join(", ");
  };

  const getUniqueProductLines = (products: CartProduct[]) => {
    const uniqueProductLinesMap: { [key: number]: CartProduct } = {};
    products.forEach(product => (uniqueProductLinesMap[product.product_line.id] = product));
    return Object.values(uniqueProductLinesMap);
  };

  const uniqueProductLines: CartProduct[] = getUniqueProductLines(order.products);
  const requestedProducts: CartProduct[] = order.products.filter(p => p.requested_item);
  const itemsCount: number = order.products.length - requestedProducts.length;

  $: retailerDistributor = $RetailerDistributorStore.find(
    rd => rd.distributor.id === order.distributor.id,
  );
  $: orderActionRequired =
    order.manual_order_action_required && retailerDistributor?.is_aoo_enabled;
</script>

<div class="fizz-card fizz-bordered fizz-stack-24">
  <div class="fizz-card fizz-card-padded fizz-border-bottom">
    <div class="fizz-space-between fizz-flex-row-responsive fizz-stack-16">
      <div>
        <p class="fizz-text-strong fizz-text-large">{order.distributor.name}</p>
        {#if isOHLQOrder(order.distributor.id)}
          {#await getStockSiteLocation(order.distributor.id, order.distributor.account_number) then data}
            <p class="fizz-text-normal">Sent to: {data.name || ""}</p>
          {/await}
        {:else}
          <p class="fizz-text-normal">Sent to: {salesRepsString(order)}</p>
        {/if}
      </div>
      <div class="fizz-flex-no-shrink">
        <a href="/orders/{order.id}" target="_blank" rel="noopener noreferrer">
          View Order Details
        </a>
      </div>
    </div>
    {#if orderActionRequired && !showStreamlinedAOOActionRequired}
      <div class="fizz-alert-warning fizz-stack-24">
        <p>
          <strong>Action Required:</strong>
          Orders to this distributor <strong>must be sent</strong>through your email.
          <UIRouterLink stateName="layout.order_details" params={{ orderId: order.id }}>
            Send this order separately.
          </UIRouterLink>
        </p>
      </div>
    {/if}
  </div>
  <div class="fizz-card fizz-card-padded">
    <div class="fizz-flex-row-32 fizz-stack-16">
      <div>
        <p class="fizz-text-strong fizz-text-medium">Order Number</p>
        <p class="fizz-text-medium">#{order.id}</p>
      </div>
      <div>
        <p class="fizz-text-strong fizz-text-medium">Items</p>
        <p class="fizz-text-medium">{itemsCount}</p>
      </div>
      {#if requestedProducts.length}
        <div>
          <p class="fizz-text-strong fizz-text-medium">Requests</p>
          <p class="fizz-text-medium">{requestedProducts.length}</p>
        </div>
      {/if}
      <div>
        <p class="fizz-text-strong fizz-text-medium">Est. Total</p>
        <p class="fizz-text-medium">
          {#if order.total > 0}
            <FormatMoney input={order.total} />
          {:else}
            N/A
          {/if}
        </p>
      </div>
    </div>
    <div class="fizz-flex-row-8 fizz-flex-wrap" class:fizz-stack-16={requestedProducts.length}>
      {#each uniqueProductLines as product}
        <div class="order-image-container fizz-stack-16">
          <ProductImage
            width={75}
            height={100}
            product={product.product_line}
            fizz={true}
            className={"fizz-stack-16"}
          />
        </div>
      {/each}
    </div>
    {#if requestedProducts.length}
      <RequestedItemBanner />
    {/if}
  </div>
</div>
